import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["content", "inline", "floating"]

  connect() {
    this.formNode = this.inlineTarget.firstElementChild
    if (!this.formNode) return

    this.confirmationCheckbox = this.findConfirmationCheckbox()
    this.hasReachedBottom = false
    this.scrollHandler = this.scheduleLockStateUpdate.bind(this)

    this.observer = new IntersectionObserver(
      ([entry]) => this.toggleFloating(!entry.isIntersecting),
      { threshold: 0 }
    )

    this.observer.observe(this.inlineTarget)
    this.toggleFloating(!this.isInlineVisible())
    this.updateConfirmationLockState()

    window.addEventListener("scroll", this.scrollHandler, { passive: true })
    window.addEventListener("resize", this.scrollHandler, { passive: true })
  }

  disconnect() {
    this.observer?.disconnect()
    window.removeEventListener("scroll", this.scrollHandler)
    window.removeEventListener("resize", this.scrollHandler)

    if (this.formNode && this.formNode.parentElement === this.floatingTarget) {
      this.inlineTarget.appendChild(this.formNode)
    }
  }

  toggleFloating(shouldFloat) {
    if (!this.formNode) return

    if (shouldFloat) {
      if (this.formNode.parentElement !== this.floatingTarget) {
        this.floatingTarget.appendChild(this.formNode)
      }

      this.floatingTarget.classList.remove("hidden")
      this.contentTarget.classList.add("pb-40")
      return
    }

    if (this.formNode.parentElement !== this.inlineTarget) {
      this.inlineTarget.appendChild(this.formNode)
    }

    this.floatingTarget.classList.add("hidden")
    this.contentTarget.classList.remove("pb-40")
  }

  isInlineVisible() {
    const rect = this.inlineTarget.getBoundingClientRect()
    return rect.top < window.innerHeight && rect.bottom > 0
  }

  findConfirmationCheckbox() {
    return this.formNode.querySelector('input[type="checkbox"][name*="confirmation_of_truth"][data-form-validator-target="field"]')
  }

  scheduleLockStateUpdate() {
    if (this.lockStateUpdateQueued || this.hasReachedBottom) return

    this.lockStateUpdateQueued = true
    window.requestAnimationFrame(() => {
      this.lockStateUpdateQueued = false
      this.updateConfirmationLockState()
    })
  }

  updateConfirmationLockState() {
    if (!this.confirmationCheckbox) return

    if (!this.hasReachedBottom && this.isScrolledToBottom()) {
      this.hasReachedBottom = true
    }

    this.setCheckboxLocked(!this.hasReachedBottom)
  }

  isScrolledToBottom() {
    const threshold = 8
    const scrolled = window.scrollY + window.innerHeight
    const totalHeight = document.documentElement.scrollHeight
    return scrolled >= totalHeight - threshold
  }

  setCheckboxLocked(locked) {
    const checkbox = this.confirmationCheckbox
    checkbox.disabled = locked

    if (locked) {
      checkbox.checked = false
    }

    const wrapper = checkbox.closest("label")
    if (wrapper) {
      wrapper.classList.toggle("cursor-not-allowed", locked)
      wrapper.classList.toggle("opacity-70", locked)

      const labelSpan = wrapper.querySelector("span")
      if (labelSpan) {
        labelSpan.classList.toggle("cursor-not-allowed", locked)
        labelSpan.classList.toggle("opacity-60", locked)
      }
    }

    checkbox.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
